import { TextField } from '@mui/material';
import Panel from '../reusable/Panel';
import AddIcon from '../assets/eoa-configuration-add.svg';
import RemoveIcon from '../assets/eoa-configuration-remove.svg';
import { decimalNumberError } from './fieldValidation';
import {
  COLLEGE_ELIGIBILITY_SUBJECTS,
  ELIGIBILITY_COLUMN_IDS,
  EoaaInfo2Form,
  EligibilityColumnId,
  EligibilitySubject,
  columnHeader,
  setIheName,
  updateCell,
} from './eoaaInfo2Form';

interface EoaaInfo2FieldsProps {
  value: EoaaInfo2Form;
  onChange: (value: EoaaInfo2Form) => void;
}

const EligibilityCellFields = ({
  form,
  columnId,
  subject,
  onChange,
}: {
  form: EoaaInfo2Form;
  columnId: EligibilityColumnId;
  subject: EligibilitySubject;
  onChange: (value: EoaaInfo2Form) => void;
}) => {
  const cell = form.cells[columnId][subject];
  const creditsHelperText = decimalNumberError(cell.credits);

  return (
    <div className="eoa-configuration-eligibility-cell">
      <TextField
        className="eoa-configuration-eligibility-credits"
        placeholder="# credits"
        value={cell.credits}
        error={Boolean(creditsHelperText)}
        helperText={creditsHelperText}
        onChange={(event) => onChange(updateCell(form, columnId, subject, { credits: event.target.value }))}
        inputProps={{ inputMode: 'decimal' }}
      />
      {cell.noteOpen ? (
        <>
          <TextField
            className="eoa-configuration-eligibility-note"
            placeholder="Add note"
            multiline
            minRows={2}
            value={cell.note}
            onChange={(event) => onChange(updateCell(form, columnId, subject, { note: event.target.value }))}
          />
          <button
            type="button"
            className="eoa-configuration-add-link"
            onClick={() => onChange(updateCell(form, columnId, subject, { note: '', noteOpen: false }))}
          >
            <img src={RemoveIcon} alt="" width={18} height={18} />
            Remove note
          </button>
        </>
      ) : (
        <button
          type="button"
          className="eoa-configuration-add-link"
          onClick={() => onChange(updateCell(form, columnId, subject, { noteOpen: true }))}
        >
          <img src={AddIcon} alt="" width={18} height={18} />
          Add notes
        </button>
      )}
    </div>
  );
};

const EoaaInfo2Fields = ({ value, onChange }: EoaaInfo2FieldsProps) => (
  <>
    <Panel className="eoa-configuration-card">
      <h3 className="eoa-configuration-card-title">EOAA information: Colleges</h3>
      <div className="eoa-configuration-fields">
        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">
            Which IHEs do you want to use as the basis for XQ’s college eligibility requirements?
          </span>
          <div className="eoa-configuration-removable-list eoa-configuration-ihe-list">
            <TextField
              className="eoa-configuration-period-name-input"
              placeholder="School name"
              value={value.iheNames[0]}
              onChange={(event) => onChange(setIheName(value, 0, event.target.value))}
            />
            <TextField
              className="eoa-configuration-period-name-input"
              placeholder="School name"
              value={value.iheNames[1]}
              onChange={(event) => onChange(setIheName(value, 1, event.target.value))}
            />
          </div>
        </div>
      </div>
    </Panel>

    <Panel className="eoa-configuration-card">
      <h3 className="eoa-configuration-card-title">EOAA information: college eligibility</h3>
      <div className="eoa-configuration-fields">
        <div className="eoa-configuration-field">
          <span className="eoa-configuration-field-label">
            For each of the subjects listed below, please fill the number of credits required.
          </span>
          <div className="eoa-configuration-eligibility-table" role="table">
            <div className="eoa-configuration-eligibility-row eoa-configuration-eligibility-row--header" role="row">
              <span role="columnheader">Subject</span>
              {ELIGIBILITY_COLUMN_IDS.map((columnId) => (
                <span key={columnId} role="columnheader">
                  {columnHeader(value, columnId)}
                </span>
              ))}
            </div>
            {COLLEGE_ELIGIBILITY_SUBJECTS.map((subject) => (
              <div key={subject} className="eoa-configuration-eligibility-row" role="row">
                <span className="eoa-configuration-eligibility-subject" role="cell">
                  {subject}
                </span>
                {ELIGIBILITY_COLUMN_IDS.map((columnId) => (
                  <div key={columnId} role="cell">
                    <EligibilityCellFields form={value} columnId={columnId} subject={subject} onChange={onChange} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  </>
);

export default EoaaInfo2Fields;
